import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DealerRecommedsItem,
  DealerRecommendsRequestBody,
  RecommendationItem,
  RecommendationRequestBody,
  RecommendationResponse,
  RecommendationPutBody,
  RecommendationCreateBody,
  RecommendationFigure,
  RecommendationSliceState,
  ItemToCart,
} from "./type";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { PageSizeType } from "@/app/model/types";

const initialState: RecommendationSliceState = {
  recommendations: [],
  dealerRecommendations: [],
  filters: [],
  error: null,
  sortRules: {
    type: "desc",
    field: "VendorCode",
  },
  isLoading: false,
  isMiniLoading: false,
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeRec") as PageSizeType) || 20,
  totalCount: 0,
  excludeItems: [],
  figures: [],
  currentBrand: null,
  itemsToCart: [],
  selectedProductsId: [],
  filalsIdToShow: [],
};

// Fetch Recommendations
export const fetchDealerRecommendations = createAsyncThunk(
  "recommendation/fetchDealerRecommendations",
  async (
    requestBody: DealerRecommendsRequestBody | "",
    { rejectWithValue },
  ) => {
    let body = {
      pagination: {
        pageSize: +localStorage.getItem("psizeRec") || 20,
        pageNumber: 1,
      },
    };

    if (requestBody) {
      const { pagination } = requestBody;

      body = {
        pagination,
      };
    }
    try {
      const response = await $api.post<DealerRecommedsItem[]>(
        "/recommendation/dealer",
        body,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);
export const fetchRecommendations = createAsyncThunk(
  "recommendation/fetchRecommendations",
  async (requestBody: RecommendationRequestBody | "", { rejectWithValue }) => {
    let body = {
      pagination: {
        pageSize: +localStorage.getItem("psizeRec") || 20,
        pageNumber: 1,
      },
      filters: [] as object[],
    };

    if (requestBody) {
      const { filters, pagination } = requestBody;

      body = {
        pagination,
        filters,
      };
    }
    try {
      const response = await $api.post<RecommendationResponse>(
        "/recommendation",
        body,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Update Recommendation
export const updateRecommendation = createAsyncThunk(
  "recommendation/updateRecommendation",
  async (requestBody: RecommendationPutBody, { rejectWithValue }) => {
    try {
      const response = await $api.put("/recommendation", requestBody);
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Create Recommendation
export const createRecommendation = createAsyncThunk(
  "recommendation/createRecommendation",
  async (requestBody: RecommendationCreateBody, { rejectWithValue }) => {
    try {
      const response = await $api.post("/recommendation/create", requestBody);
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Delete Recommendation
export const deleteRecommendation = createAsyncThunk(
  "recommendation/deleteRecommendation",
  async (brandId: number, { rejectWithValue }) => {
    try {
      const response = await $api.delete(`/recommendation/${brandId}`);
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Get Figures by Brand
export const fetchFiguresByBrand = createAsyncThunk(
  "recommendation/fetchFiguresByBrand",
  async (brandId: number, { rejectWithValue }) => {
    try {
      const response = await $api.get<RecommendationFigure[]>(
        `/recommendation/brand/${brandId}/figure`,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const recommendationSlice = createSlice({
  name: "recommendation",
  initialState,
  reducers: {
    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.searchCode = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    setCurrentBrand(state, action: PayloadAction<RecommendationItem | null>) {
      state.currentBrand = action.payload;
    },
    clearSelectedProductsId(state) {
      state.selectedProductsId = initialState.selectedProductsId;
    },
    setItemsToCart(state, action: PayloadAction<ItemToCart[]>) {
      state.itemsToCart = action.payload;
    },
    changeFilialsIdToShow(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.filalsIdToShow.includes(id)) {
        state.filalsIdToShow = state.filalsIdToShow.filter((i) => i !== id);
      } else {
        state.filalsIdToShow.push(id);
      }
    },
    clearFilialsIdToShow(state) {
      state.filalsIdToShow = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch Recommendations
    builder.addCase(fetchRecommendations.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchRecommendations.fulfilled,
      (state, action: PayloadAction<RecommendationResponse>) => {
        state.recommendations = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.isLoading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchRecommendations.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchDealerRecommendations.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchDealerRecommendations.fulfilled,
      (state, action: PayloadAction<DealerRecommedsItem[]>) => {
        state.dealerRecommendations = action.payload;
        state.isLoading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchDealerRecommendations.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update Recommendation
    builder.addCase(updateRecommendation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateRecommendation.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateRecommendation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Create Recommendation
    builder.addCase(createRecommendation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createRecommendation.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createRecommendation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete Recommendation
    builder.addCase(deleteRecommendation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteRecommendation.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteRecommendation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Figures by Brand
    builder.addCase(fetchFiguresByBrand.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchFiguresByBrand.fulfilled,
      (state, action: PayloadAction<RecommendationFigure[]>) => {
        state.figures = action.payload;
        state.isMiniLoading = false;
      },
    );
    builder.addCase(fetchFiguresByBrand.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });
  },
});
export const {
  setSortRules,
  setSearchCode,
  setPageSize,
  setCurrentBrand,
  clearSelectedProductsId,
  setItemsToCart,
  changeFilialsIdToShow,
  clearFilialsIdToShow,
} = recommendationSlice.actions;
